import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ArrowUpDown } from 'lucide-react';
import { posts } from '../../constants/posts';
import Layout from '../../components/common/Layout';
import BlogCard from './BlogCard';

const BlogListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [expandedPost, setExpandedPost] = useState<string | null>(null);
  const expandedCardRef = useRef<HTMLDivElement>(null);

  // Scroll hacia la card expandida cuando cambia
  useEffect(() => {
    if (expandedPost && expandedCardRef.current) {
      // Pequeño delay para que la animación de expansión comience primero
      setTimeout(() => {
        expandedCardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [expandedPost]);

  const sortedPosts = useMemo(() => {
    const allPosts = [...posts];
    return sortOrder === 'newest' ? allPosts.reverse() : allPosts;
  }, [sortOrder]);

  const filteredPosts = useMemo(() => {
    if (!searchTerm) return sortedPosts;
    const term = searchTerm.toLowerCase();
    return sortedPosts.filter(
      (post) =>
        post.title.toLowerCase().includes(term) ||
        post.excerpt.toLowerCase().includes(term)
    );
  }, [searchTerm, sortedPosts]);

  // Expandir el primer post solamente en la primera carga
  const initialExpandedSet = useRef(false);
  useEffect(() => {
    if (!initialExpandedSet.current && filteredPosts.length > 0) {
      setExpandedPost(filteredPosts[0].slug);
      initialExpandedSet.current = true;
    }
  }, [filteredPosts]);

  return (
    <Layout>
      <section id="blog" className="pt-24 pb-16 md:py-28 lg:py-32 bg-stone-50 min-h-screen">
        <div className="container mx-auto px-4 md:px-8">
          
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6 border-b border-contessa-200 pb-6">
            <div className="max-w-2xl">
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-contessa-800 mb-4">
                El Blog de <span className="text-contessa-600">Marha</span>
              </h1>
              <p className="text-lg text-stone-600">
                Reflexiones, información y herramientas para acompañar tu maternidad, crianza y salud mental.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto items-stretch md:items-center">
              <button
                onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
                className="flex items-center justify-center space-x-2 px-5 py-3 rounded-full border border-contessa-200 bg-white text-contessa-600 hover:bg-contessa-50 hover:border-contessa-300 transition-all font-medium text-sm whitespace-nowrap shadow-sm group"
              >
                <ArrowUpDown className="w-4 h-4 group-hover:text-contessa-700" />
                <span>{sortOrder === 'newest' ? 'Más recientes' : 'Más antiguos'}</span>
              </button>

              <div className="w-full md:w-auto relative group flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-contessa-400 group-focus-within:text-contessa-600 transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar artículos..."
                  className="w-full md:w-80 pl-10 pr-4 py-3 rounded-full border border-contessa-200 bg-white focus:outline-none focus:ring-2 focus:ring-contessa-500 focus:border-transparent transition-all shadow-sm group-hover:shadow-md"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {searchTerm && (
            <p className="mb-6 text-stone-500">
              Encontrados {filteredPosts.length} artículos {filteredPosts.length === 0 && '😔'}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {filteredPosts.map((post) => {
              const isExpanded = expandedPost === post.slug;
              
              return (
                <BlogCard
                  key={post.slug}
                  post={post}
                  isExpanded={isExpanded}
                  onExpand={() => setExpandedPost(post.slug)}
                  onCollapse={() => setExpandedPost(null)}
                  cardRef={isExpanded ? expandedCardRef : null}
                />
              );
            })}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-stone-200">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-stone-100 mb-4 text-stone-400">
                <Search className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold text-stone-700">No encontramos artículos</h3>
              <p className="text-stone-500 mt-2">Intenta buscar con otros términos o palabras clave.</p>
              <button 
                onClick={() => setSearchTerm('')}
                className="mt-6 px-6 py-2 bg-contessa-500 text-white rounded-full hover:bg-contessa-600 transition-colors"
              >
                Ver todos los artículos
              </button>
            </div>
          )}

        </div>
      </section>
    </Layout>
  );
};

export default BlogListPage;
